import React, { useEffect, useMemo, useState } from 'react';
import ProductModal from '../modals/ProductModal';
import { deleteProduct, getAllProducts, setProductActive } from '../services/ProductService';
import { formatEur } from '../utils/salesPipeline';
import { extractApiError } from '../utils/apiError';
import { useConfirm, useToast } from '../context/UIContext';
import '../styles/Products.css';

export default function Products() {
    const toast = useToast();
    const confirm = useConfirm();
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    useEffect(() => { loadProducts(); }, []);

    const loadProducts = () => {
        setIsLoading(true);
        // Always fetch everything; the toggle only decides what is displayed.
        getAllProducts({ includeInactive: true })
            .then(data => { setProducts(data); setLoadError(''); })
            .catch(() => setLoadError('Produkte konnten nicht geladen werden. Bitte versuche es erneut.'))
            .finally(() => setIsLoading(false));
    };

    const categories = useMemo(
        () => [...new Set(products.map(p => p.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de')),
        [products]
    );

    const filtered = useMemo(() => products.filter(p => {
        const term = search.trim().toLowerCase();
        const matchSearch = !term
            || (p.name || '').toLowerCase().includes(term)
            || (p.sku || '').toLowerCase().includes(term);
        const matchCategory = !categoryFilter || p.category === categoryFilter;
        const matchActive = showInactive || p.isActive;
        return matchSearch && matchCategory && matchActive;
    }), [products, search, categoryFilter, showInactive]);

    const inactiveCount = products.filter(p => !p.isActive).length;

    const handleToggleActive = (product) => {
        setProductActive(product.id, !product.isActive)
            .then(loadProducts)
            .catch(err => toast(extractApiError(err, 'Status konnte nicht geändert werden.')));
    };

    const handleDelete = async (product) => {
        const ok = await confirm(
            `"${product.name}" wirklich löschen? Bereits verkaufte Positionen bleiben in ihren Deals erhalten.`,
            { confirmLabel: 'Löschen', danger: true }
        );
        if (!ok) return;
        deleteProduct(product.id)
            .then(loadProducts)
            .catch(err => toast(extractApiError(err, 'Löschen fehlgeschlagen.')));
    };

    const handleSave = () => {
        setShowModal(false);
        setEditProduct(null);
        loadProducts();
    };

    return (
        <div className="products-page">
            {loadError && (
                <div className="alert alert--error" style={{ marginBottom: 16 }}>
                    {loadError}
                    <button className="alert__close" onClick={loadProducts} style={{ marginLeft: 12 }}>
                        Erneut versuchen
                    </button>
                </div>
            )}

            <div className="products-hero">
                <div>
                    <h2 className="products-hero-title">Produkte</h2>
                </div>
                <span className="products-count-badge">
                    {products.filter(p => p.isActive).length} aktiv
                    {inactiveCount > 0 && ` · ${inactiveCount} inaktiv`}
                </span>
            </div>

            <div className="products-toolbar">
                <input
                    type="text"
                    className="input_filter"
                    placeholder="Produkt oder Artikelnummer suchen…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select className="input" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                    <option value="">Alle Kategorien</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="acq-checkbox">
                    <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
                    Inaktive anzeigen
                </label>
                <button className="button products-add-btn" onClick={() => { setEditProduct(null); setShowModal(true); }}>
                    + Neues Produkt
                </button>
            </div>

            <div className="products-table-wrap">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Produkt</th>
                            <th>Artikelnummer</th>
                            <th>Kategorie</th>
                            <th>Einheit</th>
                            <th className="products-td-right">Listenpreis netto</th>
                            <th className="products-td-right">MwSt.</th>
                            <th>Status</th>
                            <th>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(p => (
                            <tr key={p.id} className={p.isActive ? '' : 'products-row-inactive'}>
                                <td>
                                    <span className="products-name">{p.name}</span>
                                    {p.description && <span className="products-desc">{p.description}</span>}
                                </td>
                                <td>{p.sku || '—'}</td>
                                <td>{p.category || '—'}</td>
                                <td>{p.unit}</td>
                                <td className="products-td-right products-price">
                                    {formatEur(p.listPrice, { decimals: 2 })}
                                </td>
                                <td className="products-td-right">{p.vatRate} %</td>
                                <td>
                                    <span className={`products-status ${p.isActive ? 'is-active' : 'is-inactive'}`}>
                                        {p.isActive ? 'Aktiv' : 'Inaktiv'}
                                    </span>
                                </td>
                                <td className="products-actions">
                                    <button className="button-warning" onClick={() => { setEditProduct(p); setShowModal(true); }}>
                                        Bearbeiten
                                    </button>
                                    <button className="button button-secondary" onClick={() => handleToggleActive(p)}>
                                        {p.isActive ? 'Deaktivieren' : 'Aktivieren'}
                                    </button>
                                    <button className="button-danger" onClick={() => handleDelete(p)}>
                                        Löschen
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={8} className="products-empty">
                                    {isLoading
                                        ? 'Daten werden geladen…'
                                        : products.length === 0
                                            ? 'Noch keine Produkte im Katalog. Lege das erste Produkt an, um es im Vertrieb verkaufen zu können.'
                                            : 'Keine Produkte gefunden.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <ProductModal
                    product={editProduct}
                    onClose={() => { setShowModal(false); setEditProduct(null); }}
                    onSave={handleSave}
                />
            )}
        </div>
    );
}
